"use client";

import {
	faCheck,
	faChevronRight,
	faExclamation,
	faInfo,
	faTimes,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Toast as ToastPrimitive } from "radix-ui";
import React, { useMemo, type CSSProperties, type ReactNode } from "react";
import { cn } from "../../..";
import { useNotifications } from "../../context/NotificationContext";
import { DEFAULT_DURATION } from "../../context/NotificationProvider";
import { Typography } from "../typography/Typography";
import style from "./style.module.css";

export interface ToastProps extends Omit<ToastPrimitive.ToastProps, "title"> {
	identifier?: number;

	/**
	 * Optional subtitle when title alone isn't enough
	 * Renders as `ToastPrimitive.Title`
	 */
	subtitle?: ReactNode;

	/**
	 * The toast message.
	 * Renders as `ToastPrimitive.Description`
	 */
	title: ReactNode;

	/**
	 * Styling variants of toast
	 * @defaultValue 'info'
	 */
	variant?: "info" | "warning" | "danger" | "success";

	/**
	 * An action that is safe to ignore to ensure users are not expected
	 * to complete tasks with unexpected side effects as a result of a time limit.
	 * When obtaining a user response is necessary,
	 * portal an `AlertDialog` styled as a toast into the viewport instead.
	 */
	action?: ToastPrimitive.ToastActionProps;
}

export function Toast(props: ToastProps) {
	const { identifier, variant, title, subtitle, action, ...remainingProps } = props;

	const { remove } = useNotifications();

	const getNotificationVariant = () => {
		switch (variant) {
			case "warning":
				return { icon: faExclamation, color: "bg-warning" };
			case "danger":
				return { icon: faTimes, color: "bg-danger" };
			case "success":
				return { icon: faCheck, color: "bg-success" };
			default:
				return { icon: faInfo, color: "bg-primary" };
		}
	};

	const [paused, setPaused] = React.useState(false);
	const { icon, color } = getNotificationVariant();
	const swipeInDuration = 150;
	const duration = useMemo(
		() => (remainingProps.duration ?? DEFAULT_DURATION) - swipeInDuration,
		[remainingProps.duration],
	);

	function Content({ variant }: { variant: "close" | "action" }) {
		return (
			<>
				<div className="mr-4 flex flex-col space-y-1 text-left leading-none">
					<ToastPrimitive.Description asChild>
						<Typography variant="paragraph">{title}</Typography>
					</ToastPrimitive.Description>
					{!!subtitle && (
						<ToastPrimitive.Title asChild>
							<Typography variant="paragraph" color="text-shades-dark">
								{subtitle}
							</Typography>
						</ToastPrimitive.Title>
					)}
				</div>
				<div className="flex">
					<FontAwesomeIcon icon={variant === "action" ? faChevronRight : faTimes} fixedWidth />
				</div>
			</>
		);
	}

	return (
		<ToastPrimitive.Root
			forceMount
			onPause={() => setPaused(true)}
			onResume={() => setPaused(false)}
			onOpenChange={() => setTimeout(() => remove(identifier), 100)}
			className={cn("flex cursor-grab overflow-hidden rounded bg-white shadow-md", style.root)}
			style={{ "--swipe-duration": `${swipeInDuration}ms` } as CSSProperties}
			{...remainingProps}
		>
			<div className={cn("flex items-center justify-center p-4 text-white ", color)}>
				<FontAwesomeIcon icon={icon} fixedWidth />
			</div>
			<div className="flex w-full flex-col">
				{action ? (
					<ToastPrimitive.Action
						{...action}
						className="flex w-full grow flex-row items-center justify-between px-4 py-2 hover:bg-shades-light focus-visible:bg-shades-light motion-safe:transition-colors"
					>
						<Content variant="action" />
					</ToastPrimitive.Action>
				) : (
					<ToastPrimitive.Close className="flex w-full grow flex-row items-center justify-between px-4 py-2 hover:bg-shades-light focus-visible:bg-shades-light motion-safe:transition-colors">
						<Content variant="close" />
					</ToastPrimitive.Close>
				)}

				<div className="h-[3px] w-full">
					<div
						data-paused={paused}
						className={cn("h-full origin-left transform-gpu", color, style.progress)}
						style={{ animationDuration: `${duration}ms` }}
					/>
				</div>
			</div>
		</ToastPrimitive.Root>
	);
}
